<template>
    <div class="position-relative">
        <div>
            <h2 class="h4">
                {{ __('customers.contracts.instance_settings') }}
            </h2>
        </div>
        <DFormRow
            :label="getLabelExternalId"
            :error="errors.external_id"
            :is-required="true"
        >
            <DInput
                id="external_id"
                :model-value="externalId"
                maxlength="255"
                :description="getHint"
                :disabled="contract.is_live === true"
                @update:modelValue="$emit('update:externalId', $event)"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.contracts.fulfillment_account')"
            :error="errors.fulfillment_account_id"
            :is-required="true"
        >
            <DFieldLoading
                v-if="isLoadingFulfillmentAccounts"
                :is-loading="isLoadingFulfillmentAccounts"
            />
            <DSelect
                id="fulfillment_account_id"
                :model-value="fulfillmentAccountId"
                :options="fulfillmentAccounts"
                :disabled="isLoadingFulfillmentAccounts || contract.is_live === true"
                @update:modelValue="$emit('update:fulfillmentAccountId', $event)"
            />
        </DFormRow>
        <DFormRow
            v-if="userPermissions.includes('contract.dns_provider.edit') && contract.type === 'domain'"
            :label="__('customers.contracts.dns_provider')"
            :error="errors['dns_provider_id']"
        >
            <DSelect
                id="dns_provider_id"
                :model-value="dnsProviderId"
                :options="dnsProviders"
                @update:modelValue="$emit('update:dnsProviderId', $event)"
            />
        </DFormRow>
        <DFormRow
            v-for="(row, index) in instanceSettings"
            :key="index"
            :label="row.name"
            :is-required="true"
            :error="getErrorMessage(row)"
        >
            <DSwitch
                v-if="row.type === 'bool'"
                :id="`contract-option-${row.name}`"
                v-model="instanceSettings[row.name].value"
                color="success"
                variant="3d"
                :class="{'is-invalid': !!errors[`config_values.${row.name}.value`]}"
                :description="row.description"
            />
            <DInput
                v-if="['string', 'integer', 'email'].includes(row.type)"
                :id="`contract-option-${row.name}`"
                v-model="instanceSettings[row.name].value"
                :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                :description="row.description"
            />
            <DFormTextarea
                v-if="row.type === 'text'"
                :id="`contract-option-${row.name}`"
                v-model="instanceSettings[row.name].value"
                :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                :description="row.description !== null ? row.description : __('messages.select_value')"
                rows="8"
            />
            <DSelect
                v-if="row.type === 'list'"
                :id="`contract-option-${row.name}`"
                v-model="instanceSettings[row.name].value"
                :options="row.options"
                :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                :description="row.description"
                :placeholder="row.description"
            />
            <DContractOptionModal
                v-if="row.type === 'modal'"
                :id="`contract-option-${instanceSettings[row.name].name}`"
                v-model="instanceSettings[row.name].value"
                :name="row.name"
                :disabled="contract.id && instanceSettings[row.name].editable"
                :display-value="instanceSettings[row.name].display_value ?? instanceSettings[row.name].value"
                :fulfillment-module="fulfillmentModule"
                @update:modelValue="(value) => setModalValue(row.name, value)"
            />
        </DFormRow>
    </div>
</template>

<script>
import DFieldLoading from '~@/Components/Common/DFieldLoading'
import DContractOptionModal from '~@/Components/Fulfillment/Options/DContractOptionModal'

export default {
    name: 'InstanceSettings',
    components: { DFieldLoading, DContractOptionModal },
    props: {
        configValues: {
            type: Object,
            default: () => { return {} }
        },
        externalId: {
            type: [String, Number],
            default: null
        },
        fulfillmentAccountId: {
            type: [String, Number],
            default: null
        },
        form: {
            type: Object,
            default: () => { return {} }
        },
        contract: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        fulfillmentAccounts: {
            type: Array,
            default: () => { return [] }
        },
        isLoadingFulfillmentAccounts: {
            type: Boolean,
            default: false
        },
        externalIdMapping: {
            type: Object,
            default: () => { return {} }
        },
        fulfillmentModule: {
            type: String,
            default: null
        },
        isPreLiveContract: {
            type: Boolean,
            default: false
        },
        dnsProviderId: {
            type: [String, Number],
            default: null
        },
        dnsProviders: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['update:configValues', 'update:externalId', 'update:fulfillmentAccountId', 'update:dnsProviderId'],
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getHint () {
            const modName = this.fulfillmentModule
            const hint = (modName in this.externalIdMapping) ? this.externalIdMapping[modName].hint : this.externalIdMapping.anyOtherModule.hint
            if (hint === '') {
                return ''
            }
            return this.__('tools.contract.hintIntro') + ' ' + hint
        },
        getLabelExternalId () {
            const langKey = 'tools.contract.name.' + this.fulfillmentModule
            const langText = this.__(langKey)
            if (langKey !== langText) {
                return langText
            }
            return this.__('tools.contract.name.anyOtherModule')
        },
        instanceSettings: {
            get () {
                // Show instance settings if existing contract is enabled or, it's a live contract
                if (this.form.existing_contract === true || this.isPreLiveContract === false) {
                    const instanceSettings = {}

                    for (const option of Object.values(this.form.config_values)) {
                        if (option.is_instance_setting === true) {
                            instanceSettings[option.name] = option
                        }
                    }

                    return instanceSettings
                }

                return {}
            },
            set (value) {
                this.$emit('update:configValues', value)
            }
        }
    },
    methods: {
        getErrorMessage (row) {
            if (!this.errors[`config_values.${row.name}.value`]) {
                return this.errors[`config_values.${row.name}.value`]
            }

            return this.errors[`config_values.${row.name}.value`].replaceAll('config values.', '').replaceAll('.value', '')
        },
        setModalValue (key, value) {
            this.instanceSettings[key].value = value.value
            this.instanceSettings[key].display_value = value.display_value
        }
    }
}
</script>
